import * as React from "react"
import { Check, Copy } from "lucide-react"
import { cn } from "@/lib/utils"

interface CopyMarkdownButtonProps {
  markdown: string
  label?: string
  variant?: "compact" | "default"
  className?: string
}

// Small primitive used in two places on /docs/api:
//   * The page header — "Copy all as markdown".
//   * Each section's title row — "Copy section".
//
// Uses navigator.clipboard.writeText. Falls back to a textarea-based
// copy when the Clipboard API isn't available (older Safari, insecure
// contexts). Shows a transient "Copied!" state for ~1.5s after success.
export function CopyMarkdownButton({
  markdown,
  label = "Copy as markdown",
  variant = "default",
  className,
}: CopyMarkdownButtonProps) {
  const [copied, setCopied] = React.useState(false)
  const timeoutRef = React.useRef<number | null>(null)

  React.useEffect(
    () => () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
    },
    [],
  )

  const onClick = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown)
      } else {
        fallbackCopy(markdown)
      }
      setCopied(true)
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Last-resort fallback: stuff into a textarea and execCommand.
      fallbackCopy(markdown)
      setCopied(true)
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1500)
    }
  }

  const compact = variant === "compact"

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-hairline bg-surface text-xs font-medium text-ink-body transition-colors hover:bg-page hover:text-ink-display",
        compact ? "px-2 py-1" : "px-3 py-1.5",
        className,
      )}
      aria-label={label}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-success-display" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
      <span>{copied ? "Copied!" : label}</span>
    </button>
  )
}

function fallbackCopy(text: string) {
  if (typeof document === "undefined") return
  const ta = document.createElement("textarea")
  ta.value = text
  ta.style.position = "fixed"
  ta.style.top = "-1000px"
  ta.style.opacity = "0"
  document.body.appendChild(ta)
  ta.focus()
  ta.select()
  try {
    document.execCommand("copy")
  } catch {
    // No-op — clipboard unavailable.
  }
  document.body.removeChild(ta)
}
